import { useEffect, useState } from "react";
import { api, type Modelo } from "@/lib/api";

type Props = {
  /** Nombre que el usuario está escribiendo (se consulta con debounce). */
  nombre: string;
  /** Clases extra del contenedor (solo se pinta cuando HAY similares). */
  className?: string;
  /** Si viene, cada similar trae un botón "Usar este" (elige el existente). */
  onUsar?: (m: Modelo) => void;
  /** Avisa al padre si hay similares (p. ej. para re-etiquetar el botón Crear). */
  onHaySimilares?: (hay: boolean) => void;
};

/**
 * Aviso NO restrictivo de modelos similares al que se va a crear — espejo
 * del aviso de caso duplicado por IMEI. Consulta el catálogo completo
 * (todas las marcas) con debounce mientras se escribe; crear siempre
 * sigue permitido.
 */
export default function AvisoModelosSimilares({
  nombre,
  className,
  onUsar,
  onHaySimilares,
}: Props) {
  const [similares, setSimilares] = useState<Modelo[]>([]);

  useEffect(() => {
    const v = nombre.trim();
    if (v.length < 2) {
      setSimilares([]);
      onHaySimilares?.(false);
      return;
    }
    // `vigente` invalida la respuesta si el nombre cambió o el componente
    // se desmontó mientras el fetch iba en vuelo: sin esto, una respuesta
    // tardía pisaba al estado nuevo (aviso obsoleto o fantasma).
    let vigente = true;
    const t = setTimeout(async () => {
      try {
        const res = await api.get<Modelo[]>(
          `/api/catalogos/modelos/similares?nombre=${encodeURIComponent(v)}`,
        );
        if (!vigente) return;
        setSimilares(res);
        onHaySimilares?.(res.length > 0);
      } catch {
        if (!vigente) return;
        setSimilares([]);
        onHaySimilares?.(false);
      }
    }, 450);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nombre]);

  if (similares.length === 0) return null;

  return (
    <div
      className={`mt-2 rounded border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-200 ${className ?? ""}`}
    >
      <p className="font-medium">⚠ Ya hay modelos que se parecen — ¿no será uno de estos?</p>
      <ul className="mt-1.5 space-y-1">
        {similares.slice(0, 4).map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>
              <span className="text-amber-400/70">{m.marca_nombre} /</span>{" "}
              <strong>{m.nombre}</strong>
            </span>
            {m.creado_por_nombre && (
              <span className="text-amber-200/60">
                agregado por {m.creado_por_nombre}
                {m.creado_el ? ` el ${new Date(m.creado_el).toLocaleDateString()}` : ""}
              </span>
            )}
            {onUsar && (
              <button
                type="button"
                onClick={() => onUsar(m)}
                className="underline hover:text-white"
              >
                Usar este
              </button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-amber-200/60">
        Si de verdad es un modelo distinto, créalo sin pendiente.
      </p>
    </div>
  );
}
